import { getOrElse } from 'src/util/values';

export function getValues(obj: any) {
  return Object.keys(obj).map((key) => obj[key]);
}

export function hasKeys(obj: any) {
  return Object.keys(obj).length > 0;
}

export function withoutNullKeys(obj: any) {
  return Object.keys(obj).reduce((trimmed, key) => {
    const value = obj[key];
    if (value != null) {
      trimmed[key] = value;
    }
    return trimmed;
  }, obj);
}

export function getKeyValues<TValue>(obj: {
  [key: string]: TValue;
}): { key: string; value: TValue }[] {
  return Object.keys(obj).map((key, index) => {
    return { key, value: obj[key] };
  });
}

/*  Function to test if an object is a plain object, i.e. is constructed
 **  by the built-in Object constructor and inherits directly from Object.prototype
 **  or null. Some built-in objects pass the test, e.g. Math which is a plain object
 **  and some host or exotic objects may pass also.
 **
 **  @param {} obj - value to test
 **  @returns {boolean} true if passes tests, false otherwise
 */
export function isPlainObject(obj: any) {
  // Basic check for Type object that's not null
  if (typeof obj === 'object' && obj !== null) {
    // If Object.getPrototypeOf supported, use it
    if (typeof Object.getPrototypeOf === 'function') {
      const proto = Object.getPrototypeOf(obj);
      return proto === Object.prototype || proto === null;
    }

    // Otherwise, use internal class
    // This should be reliable as if getPrototypeOf not supported, is pre-ES5
    return Object.prototype.toString.call(obj) === '[object Object]';
  }

  // Not an object
  return false;
}

export function clone(obj: any): any {
  return JSON.parse(JSON.stringify(obj));
}

export function firstDefinedValue<T>(...values: any[]): T | undefined {
  return values.find((v) => v != null);
}

export function pluck(key: string, defaultValue: any = null): (obj: any) => any {
  return (obj: any) => {
    const value = obj != null ? obj[key] : undefined;
    return getOrElse(value, defaultValue);
  };
}

export function addValues(obj1: { [id: string]: any }, obj2: { [id: string]: any }) {
  const result = Object.keys(obj2).reduce(
    (r, key) => {
      if (r.hasOwnProperty(key)) {
        r[key] += getOrElse(obj2[key], 0);
      }
      return r;
    },
    { ...obj1 },
  );
  return result;
}

export function sumValues(objects: { [id: string]: any }[]): { [id: string]: any } | undefined {
  if (objects.length > 0) {
    const result = objects.reduce((sum, obj, index) => {
      return index === 0 ? sum : addValues(sum, obj);
    }, objects[0]);
    return result;
  } else {
    return undefined;
  }
}

export interface PathValuePair {
  path: string[];
  value: any;
}

/**
 * Collapses an object into an array of objects containing the value of
 * each leaf node in the object tree and the path to navigate to that object
 * in array form
 */
export function collapseToPaths(obj: any) {
  return collapseToPathsImpl(obj, []);
}

export function collapseToPathsImpl(obj: any, rootPath: string[]) {
  const resultPaths: PathValuePair[] = [];
  if (obj instanceof Object) {
    Object.keys(obj)
      .map((key) => {
        const value = obj[key];
        const paths = collapseToPathsImpl(value, [...rootPath, key]);
        return paths;
      })
      .forEach((childPaths) => {
        resultPaths.push(...childPaths);
      });
  } else {
    resultPaths.push({ path: [...rootPath], value: obj });
  }
  return resultPaths;
}
